<template>
  <div class="drawing-mode-toggle mb-3">
    <!-- Pathology Selection with Mode Badge -->
    <div class="mb-3">
      <div class="d-flex justify-content-between align-items-center mb-2">
        <label class="form-label mb-0">Select Pathology to Draw</label>
        <div
          class="mode-badge"
          v-b-tooltip.hover.left
          :title="tooltipText"
        >
          <font-awesome-icon :icon="currentProject?.freehand ? 'pencil' : 'square'" />
        </div>
      </div>
      <BFormSelect
        v-model="selectedPathology"
        :options="pathologySelectOptions"
        size="sm"
        @change="handlePathologySelect"
      />

      <!-- Custom Pathology Input (shows when "Other" is selected) -->
      <BFormInput
        v-if="isOtherPathologySelected"
        v-model="customPathologyText"
        placeholder="Enter custom pathology name..."
        size="sm"
        class="mt-2 custom-pathology-input"
        :class="{ 'is-required-empty': isOtherPathologySelected && !customPathologyText.trim() }"
        @input="handleCustomPathologyInput"
      />
    </div>

    <small v-if="!selectedPathology" class="text-warning d-block mt-2">
      Please select a pathology before drawing
    </small>

    <small v-if="isOtherPathologySelected && !customPathologyText.trim()" class="text-warning d-block mt-2">
      <font-awesome-icon icon="exclamation-triangle" class="me-1" />
      Custom pathology name is required
    </small>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useStore } from 'vuex'

const store = useStore()
const selectedPathology = ref(null)
const customPathologyText = ref('')

const drawingMode = computed(() => store.state.labelerAnnotation.drawingMode)
const pathologyOptions = computed(() => store.state.labelerAnnotation.pathologyOptions)
const selectedPathologyForDrawing = computed(() => store.state.labelerAnnotation.selectedPathologyForDrawing)

// Check if "Other" is selected (case-insensitive)
const isOtherPathologySelected = computed(() => {
  return selectedPathology.value?.toLowerCase() === 'other'
})

// Get current project from the store
const currentProject = computed(() => {
  const projects = store.state.projectSubmission?.projects
  const selectedProjectId = store.state.projectSubmission?.selectedProjectId
  if (projects && selectedProjectId) {
    return projects.find(p => p._id === selectedProjectId)
  }
  return null
})

const pathologySelectOptions = computed(() => [
  { value: null, text: 'Select pathology to draw...' },
  ...pathologyOptions.value.map(p => ({
    value: p.value || p.label || p._id, // Handle both mock and real API data
    text: p.label
  }))
])

const tooltipText = computed(() => {
  const isFreehand = currentProject.value?.freehand
  const currentMode = isFreehand ? 'segmentation/freehand' : 'bounding box'
  const alternateMode = isFreehand ? 'bounding box' : 'segmentation/freehand'
  return `This project is configured for ${currentMode} annotation, should you wish to change to ${alternateMode} please contact your evaluation administrator`
})

const handlePathologySelect = () => {
  // Store the selected pathology for the next annotation
  console.log('Selected value:', selectedPathology.value)
  console.log('All pathology options:', pathologyOptions.value)

  // If "Other" is selected, wait for custom text input
  if (isOtherPathologySelected.value) {
    // Clear custom text when first selecting "Other"
    customPathologyText.value = ''
    // Don't set drawing mode yet - wait for custom text
    store.commit('labelerAnnotation/SET_DRAWING_MODE', null)
    return
  }

  // Find pathology - handle both mock data (p.value) and real API data (p.label or p._id)
  const pathology = pathologyOptions.value.find(p =>
    p.value === selectedPathology.value ||
    p.label === selectedPathology.value ||
    p._id === selectedPathology.value
  )
  console.log('Found pathology:', pathology)

  if (pathology) {
    const pathologyName = pathology.label || pathology.value
    console.log('Pathology selected:', { pathology: pathologyName, color: pathology.color })

    // Set the selected pathology
    store.commit('labelerAnnotation/SET_SELECTED_PATHOLOGY_FOR_DRAWING', {
      pathology: pathologyName,
      color: pathology.color || '#ed1a4f'
    })

    // Automatically set drawing mode based on project settings
    const mode = currentProject.value?.freehand ? 'freehand' : 'box'
    console.log('Setting drawing mode based on project:', mode)
    store.commit('labelerAnnotation/SET_DRAWING_MODE', mode)
  } else {
    console.warn('Pathology not found in options!')
  }
}

// Handle custom pathology text input
const handleCustomPathologyInput = () => {
  if (isOtherPathologySelected.value && customPathologyText.value.trim()) {
    // Use the custom text as the pathology name
    console.log('Custom pathology selected:', customPathologyText.value)

    // Set the custom pathology
    store.commit('labelerAnnotation/SET_SELECTED_PATHOLOGY_FOR_DRAWING', {
      pathology: customPathologyText.value.trim(),
      color: '#ed1a4f' // Default color for custom pathologies
    })

    // Automatically set drawing mode based on project settings
    const mode = currentProject.value?.freehand ? 'freehand' : 'box'
    console.log('Setting drawing mode based on project:', mode)
    store.commit('labelerAnnotation/SET_DRAWING_MODE', mode)
  } else if (isOtherPathologySelected.value && !customPathologyText.value.trim()) {
    // Clear drawing mode if input is empty
    store.commit('labelerAnnotation/SET_DRAWING_MODE', null)
  }
}

// Clear drawing mode when pathology is deselected
watch(selectedPathology, (newVal) => {
  if (!newVal) {
    store.commit('labelerAnnotation/SET_DRAWING_MODE', null)
    customPathologyText.value = ''
  } else if (newVal.toLowerCase() !== 'other') {
    // Clear custom text when switching away from "Other"
    customPathologyText.value = ''
  }
})

// Watch for store changes to reset the dropdown (after finishing drawing)
watch(selectedPathologyForDrawing, (newVal) => {
  if (newVal === null) {
    selectedPathology.value = null
    customPathologyText.value = ''
  }
})
</script>

<style scoped>
.drawing-mode-toggle {
  padding: 0.75rem;
  background-color: var(--color-bg-secondary);
  border: var(--border-width-card) solid var(--color-border-secondary);
  border-radius: var(--border-radius-standard);
}

.drawing-mode-toggle .form-label {
  color: #ffffff;
  font-weight: 900;
  letter-spacing: 0.05em;
}

.mode-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #f0b93b;
  font-size: 1.1rem;
  cursor: help;
  opacity: 0.8;
}

.mode-badge:hover {
  opacity: 1;
}

.text-warning {
  color: #f0b93b !important;
}

.drawing-mode-toggle :deep(.form-select) {
  background-color: #15385b;
  color: #ffffff;
  border-color: #3981c8;
}

.drawing-mode-toggle :deep(.form-select:focus) {
  border-color: #f0b93b;
  box-shadow: 0 0 0 0.2rem rgba(240, 185, 59, 0.25);
}

.drawing-mode-toggle :deep(.form-control) {
  background-color: #15385b;
  color: #ffffff;
  border-color: #3981c8;
}

.drawing-mode-toggle :deep(.form-control:focus) {
  background-color: #15385b;
  color: #ffffff;
  border-color: #f0b93b;
  box-shadow: 0 0 0 0.2rem rgba(240, 185, 59, 0.25);
}

.drawing-mode-toggle :deep(.form-control::placeholder) {
  color: #878787;
}

/* Required empty state for custom pathology input */
.custom-pathology-input.is-required-empty {
  border-color: #f0b93b !important;
  border-width: 2px !important;
  animation: pulse-warning 2s ease-in-out infinite;
  background-color: rgba(240, 185, 59, 0.05) !important;
}

.custom-pathology-input.is-required-empty:focus {
  border-color: #f0b93b !important;
  box-shadow: 0 0 0 0.25rem rgba(240, 185, 59, 0.35) !important;
}

@keyframes pulse-warning {
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(240, 185, 59, 0.4);
  }
  50% {
    box-shadow: 0 0 0 4px rgba(240, 185, 59, 0);
  }
}
</style>
